import {
  APPOINTMENT_CHANNEL,
  APPOINTMENT_STATUS,
  Appointment,
  CompletedQueueStatus,
  OpdQueueStatus,
  OtherQueueStatus,
  PatientProfile,
  QUEUE_TYPE,
  TCreateReceiptBody,
  TCreateReceiptResponse,
  TReceipt,
} from "@elixir/types";
import { TFormData } from "../types/appointment";
import { MassagedProfile, getMe } from "./bridges/methods";
import { getAgeString } from "./generic-utils";

export enum EStaffRestriction {
  ALL_PT = "ALL_PT",
  START_VISIT = "START_VISIT",
  PAY_DASH = "PAY_DASH",
  DOWNLOAD_PATIENT_EXCEL = "DOWNLOAD_PATIENT_EXCEL",
  PAY_DASH_BEYOND_TODAY = "PAY_DASH_BEYOND_TODAY",
  EDIT_RECEIPT = "EDIT_RECEIPT",
}

export const getUhidFromAptFormData = ({
  formData,
}: {
  formData: TFormData[];
}): string | null => {
  const uhidIndex = formData?.findIndex((d) => d.key.toLowerCase() === "uhid");

  return formData[uhidIndex]?.value || null;
};

export const mapReceiptResponseToStore = (
  data:
    | TCreateReceiptResponse
    | (TCreateReceiptBody["payload"] & {
        receipt_id?: number;
        receipt_url?: string;
        receipt_payment?: TCreateReceiptBody["payload"]["receipt_payments"];
      })
    | undefined
): Partial<TReceipt> => {
  return {
    additional_discount_amount: data?.additional_discount_amount,
    additional_discount_type: data?.additional_discount_type,
    additional_discount_value: data?.additional_discount_value,
    mobile: data?.mobile,
    name: data?.name,
    net_amount: data?.net_amount,
    receipt_amount: data?.receipt_amount,
    receipt_id: data?.receipt_id,
    receipt_number: data?.receipt_number,
    business_receipt_number: data?.business_receipt_number,
    receipt_payment: data?.receipt_payment || [],
    receipt_url: data?.receipt_url,
  };
};

export const getCount = (data?: Appointment[]) => {
  return (data?.length || 0).toString().padStart(2, "0");
};

export type PatientProfileWithAbha = PatientProfile & Pick<Appointment, "abha">;

export const getPatientMainInfoString = ({
  patient,
  formData,
}: {
  patient: PatientProfileWithAbha;
  formData?: TFormData[];
}) => {
  const patientInfoArray: string[] = [];
  const salutation = formData?.find((item) => item.key === "salutation")?.value;

  const patientName = `${salutation ? `${salutation} ` : ""}` + patient.fln;
  patientInfoArray.push(patientName);

  const patientGender = patient?.gen;
  if (patientGender) {
    patientInfoArray.push(patientGender);
  }

  const patientAge = getAgeString({ dob: patient.dob });
  if (patientAge) {
    patientInfoArray.push(patientAge);
  }

  return patientInfoArray;
};

export const getQueueTypeFromStatus = (
  status: APPOINTMENT_STATUS
): QUEUE_TYPE => {
  if (status === APPOINTMENT_STATUS.BOOKED) {
    return QUEUE_TYPE.BOOKED;
  } else if (OpdQueueStatus.includes(status)) {
    return QUEUE_TYPE.CHECKED_IN;
  } else if (CompletedQueueStatus.includes(status)) {
    return QUEUE_TYPE.COMPLETED;
  } else if (OtherQueueStatus.includes(status)) {
    return QUEUE_TYPE.OTHERS;
  } else {
    return QUEUE_TYPE.FOLLOWUP;
  }
};

export function getMyAccessProfileV2({
  clinicId,
}: {
  clinicId?: string;
} = {}): MassagedProfile {
  const myProfile = getMe();

  const doctors =
    clinicId && clinicId !== "all"
      ? myProfile.doctors.filter((item) => item.clinics.includes(clinicId))
      : myProfile.doctors;

  return {
    ...myProfile,
    doctors,
    hasAccessToMultipleDoctors: myProfile.doctors.length > 1,
  };
}

export const getAptsForDoctor = ({
  apts,
  doctor_oid,
}: {
  apts: Appointment[];
  doctor_oid?: string;
}) => {
  if (!doctor_oid) return apts;
  return apts.filter((apt) => apt.doctor_oid === doctor_oid);
};

export function getBookedAptChannel({ docIdInApt }: { docIdInApt: string }) {
  const me = getMe();

  const myOid = me?.user?.oid;

  if (myOid === docIdInApt) {
    return APPOINTMENT_CHANNEL.DOCTOR;
  }

  return APPOINTMENT_CHANNEL.STAFF;
}
